// src/ConsumerApp/DesignSystem/Navigation/components/CartFlyoutPopover.js

import React, { useEffect, useRef, useState } from 'react';
import CheckCircleGreen from '../../../../assets/icons/CheckCircleGreen.svg';
import X from '../../../../assets/icons/X.svg';
import ToteIcon from '../../../../assets/icons/tote.svg';

const IMAGE_BASE_URL =
  process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES || 'https://assets.brickbucket.in/';

function CartFlyoutItemImage({ src, alt }) {
  const [hasError, setHasError] = useState(false);

  if (!src || hasError) {
    return (
      <img
        src={ToteIcon}
        alt="fallback"
        style={{ width: 18, height: 18, opacity: 0.35 }}
      />
    );
  }

  return (
    <img
      src={src}
      alt={alt}
      style={styles.thumbImg}
      onError={() => setHasError(true)}
    />
  );
}

export default function CartFlyoutPopover({
  isOpen,
  onClose,
  items = [],
  resetTimerSignal = 0,
  onGoToCart,
}) {
  const [isHovered, setIsHovered] = useState(false);
  const scrollRef = useRef(null);
  const timerRef = useRef(null);

  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  // Robust 5-second timer with hover pause & in-place timer refresh
  useEffect(() => {
    if (!isOpen) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    if (isHovered) {
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (onCloseRef.current) {
        onCloseRef.current();
      }
    }, 5000);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isOpen, isHovered, resetTimerSignal]);

  // Smooth scroll down to the newest item at the bottom
  useEffect(() => {
    if (isOpen && scrollRef.current) {
      scrollRef.current.scrollTo({
        top: scrollRef.current.scrollHeight,
        behavior: 'smooth',
      });
    }
  }, [isOpen, items.length]);

  if (!isOpen || items.length === 0) return null;

  return (
    <div
      style={styles.popoverShell}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={styles.headerRow}>
        <div style={styles.headerLeft}>
          <img src={CheckCircleGreen} alt="added" style={{ width: 15, height: 15 }} />
          <span style={styles.headerTitle}>Added to Bucket</span>
        </div>
        <button
          type="button"
          style={styles.closeBtn}
          onClick={onClose}
          aria-label="Close cart preview"
        >
          <img src={X} alt="close" style={{ width: 12, height: 12, opacity: 0.5 }} />
        </button>
      </div>

      <div style={styles.itemsListScroll} ref={scrollRef}>
        {items.map((item, idx) => {
          const name = item.item_name || item.product_name || item.name || 'Building Material';
          const unitPrice = Number(item.unit_price) || 0;
          const qty = Number(item.quantity) || 1;
          const totalPrice = unitPrice * qty;

          const rawImg =
            item.item_image ||
            item.product_image ||
            item.image ||
            item.product_image_url;

          // Format leading slashes properly if present in backend string
          let cleanImgPath = rawImg;
          if (cleanImgPath && cleanImgPath.startsWith('/')) {
            cleanImgPath = cleanImgPath.slice(1);
          }

          const imgSrc = cleanImgPath
            ? cleanImgPath.startsWith('http://') || cleanImgPath.startsWith('https://') || cleanImgPath.startsWith('data:')
              ? cleanImgPath
              : `${IMAGE_BASE_URL}${cleanImgPath}`
            : null;

          return (
            <div key={item.item_id || idx} style={styles.itemRow}>
              <div style={styles.thumbBox}>
                <CartFlyoutItemImage src={imgSrc} alt={name} />
              </div>

              <div style={styles.itemMeta}>
                <span style={styles.itemName} title={name}>
                  {name}
                </span>

                <div style={styles.metaBottomRow}>
                  <span style={styles.itemQtyBadge}>Qty: {qty}</span>
                 {/*  {totalPrice > 0 && (
                    <strong style={styles.itemPrice}>
                      ₹{totalPrice.toLocaleString('en-IN')}
                    </strong>
                  )} */}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div style={styles.footerRow}>
        <button type="button" style={styles.goToCartBtn} onClick={onGoToCart}>
          <span>View Bucket</span>
          <span>→</span>
        </button>
      </div>
    </div>
  );
}

const styles = {
  popoverShell: {
    position: 'absolute',
    top: 'calc(100% + 10px)',
    right: 0,
    width: 310,
    backgroundColor: '#FFFFFF',
    borderRadius: 14,
    border: '1px solid #E2E8F0',
    boxShadow: '0 12px 32px rgba(15, 23, 42, 0.16)',
    zIndex: 9999,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxSizing: 'border-box',
    animation: 'flyoutSlideDown 0.18s cubic-bezier(0.16, 1, 0.3, 1)',
  },
  headerRow: {
    padding: '12px 14px',
    borderBottom: '1px solid #F1F5F9',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  headerTitle: {
    fontSize: 13,
    fontWeight: '800',
    color: '#16A34A',
  },
  closeBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: 4,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  itemsListScroll: {
    maxHeight: 220,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#FAF9F6',
    scrollbarWidth: 'thin',
  },
  itemRow: {
    padding: '10px 14px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    borderBottom: '1px solid #F1F5F9',
    backgroundColor: '#FFFFFF',
  },
  thumbBox: {
    width: 44,
    height: 44,
    borderRadius: 8,
    border: '1px solid #E2E8F0',
    backgroundColor: '#F8FAFC',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  },
  thumbImg: {
    width: '85%',
    height: '85%',
    objectFit: 'contain',
  },
  itemMeta: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    gap: 3,
  },
  itemName: {
    fontSize: 12.5,
    fontWeight: '600',
    color: '#0F172A',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  metaBottomRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 1,
  },
  itemQtyBadge: {
    fontSize: 11,
    fontWeight: '700',
    color: '#475569',
    backgroundColor: '#F1F5F9',
    padding: '1px 6px',
    borderRadius: 4,
  },
  itemPrice: {
    fontSize: 13,
    fontWeight: '800',
    color: '#0F172A',
  },
  footerRow: {
    padding: '12px 14px',
    backgroundColor: '#FFFFFF',
    borderTop: '1px solid #F1F5F9',
  },
  goToCartBtn: {
    width: '100%',
    backgroundColor: '#E9001E',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: 9,
    padding: '10px 14px',
    fontSize: 12.5,
    fontWeight: '800',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: 6,
    boxShadow: '0 2px 6px rgba(233, 0, 30, 0.25)',
  },
};